// Thin wrapper around fetch to centralise base URL, auth token,
// and error handling. Use TanStack Query hooks on top of these
// helpers — do not call them directly from components.

import { firebaseAuth } from '@/lib/firebase';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/// URL absoluta de um caminho da API, pra quem NÃO passa pelo `api()` —
/// hoje o link do PDF de resultados, que é um `<a href>` aberto pelo
/// navegador e não um fetch.
///
/// Existe porque um caminho relativo ignora o `VITE_API_BASE_URL` que todo
/// o resto respeita: funciona enquanto o painel e a API dividem origem (o
/// `events.*` faz proxy de `/api/*`) e quebra calado no dia em que a API
/// mudar de host — que é exatamente o cenário pro qual a variável existe.
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: unknown,
  ) {
    // Inclui o corpo (texto ou JSON serializado) na mensagem pra que
    // qualquer consumidor que mostre `error.message` exiba algo útil
    // (ex: "403 Forbidden: ..." em vez de "Failed to fetch" ou
    // "body stream already read").
    const detail =
      typeof body === 'string' && body.length > 0
        ? `: ${body}`
        : body && typeof body === 'object'
          ? `: ${JSON.stringify(body)}`
          : '';
    super(`${status} ${statusText}${detail}`);
    this.name = 'ApiError';
  }
}

/// Código de erro do backend (`{ error: "registration_has_active_tracker" }`),
/// pra tela traduzir pelo dicionário dela. Nulo quando o erro não é da API ou
/// não traz código (rede, 500 sem corpo, `error` que não é string).
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return null;
  const code = (err.body as { error?: unknown }).error;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/// Override usado APENAS por E2E (Playwright) e bootstrap-admin scripts.
/// Em runtime normal `getCurrentToken()` puxa direto do Firebase em cada
/// request — modelo pull elimina a race "request disparou antes do
/// onIdTokenChanged firar setAuthToken", que deixava DNF/ranking/etc
/// indo sem header e batendo 401.
let tokenOverride: string | null = null;

export function setAuthToken(token: string | null) {
  tokenOverride = token;
}

/// Token da sessão pra quem fala com o servidor fora do `api()`: o hub SignalR
/// do live. Mesma fonte do `api()`, incluindo o override do modo E2E; ler o
/// Firebase direto deixava o hub sem token no E2E contra a API local.
export async function getAuthToken(): Promise<string | null> {
  return getCurrentToken();
}

async function getCurrentToken(): Promise<string | null> {
  if (tokenOverride) return tokenOverride;
  if (!firebaseAuth) return null;
  // Espera o Firebase terminar de restaurar a sessão persistida antes de
  // ler `currentUser`. Sem isso, num cold load (ou refetch logo após o
  // boot) a request pode sair enquanto `currentUser` ainda é null porque o
  // restore é assíncrono → header Authorization ausente → 401 anônimo
  // (`scheme=(none)` no backend), mesmo o usuário estando logado. Um reload
  // "resolvia" só porque a sessão já estava quente. `authStateReady()`
  // resolve imediatamente quando o estado já está determinado, então o
  // custo no caminho quente é desprezível.
  await firebaseAuth.authStateReady();
  const user = firebaseAuth.currentUser;
  if (!user) return null;
  try {
    // `getIdToken()` devolve o cached enquanto válido e auto-refresha
    // quando expirado. Custo amortizado é desprezível.
    return await user.getIdToken();
  } catch {
    return null;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;
  const token = await getCurrentToken();
  const init: RequestInit = {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };
  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${path}`, init);
  if (!response.ok) {
    // Lê o body UMA vez como texto e tenta parsear como JSON. Sem isso,
    // o catch `await response.text()` falha com "body stream already read"
    // porque `response.json()` já consumiu o stream — apagando o status
    // real (ex: 403 Forbidden) da mensagem de erro.
    const text = await response.text().catch(() => '');
    let parsed: unknown = text;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }
    throw new ApiError(response.status, response.statusText, parsed);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
